/**
 * Who is signed in.
 *
 * The session itself is an httpOnly cookie the JavaScript cannot read, so
 * "am I signed in" is answered by asking the server once at startup. Until
 * that answer arrives the app renders nothing but a loading state -- rendering
 * the login page first would flash it at an already-authenticated user on
 * every refresh.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { ApiError, api, setUnauthorizedHandler } from '@/api/client'
import type { User } from '@/api/types'

interface AuthState {
  user: User | null
  /** True only until the initial session check resolves. */
  loading: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  /** Called when any request comes back 401, to drop straight to the login page. */
  onUnauthorized: () => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    api.auth
      .me()
      .then((me) => {
        if (!cancelled) setUser(me)
      })
      .catch(() => {
        // A 401 here is the normal not-signed-in case, not an error worth
        // surfacing.
        if (!cancelled) setUser(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    setUser(await api.auth.login(username, password))
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.auth.logout()
    } catch (error) {
      // Logout clears a cookie. If the request fails the local state should
      // still forget the user; there is nothing to retry.
      if (!(error instanceof ApiError)) throw error
    }
    setUser(null)
  }, [])

  const onUnauthorized = useCallback(() => setUser(null), [])

  // Any 401 from anywhere drops the local user, which sends the router to the
  // login page. Registered once, for the lifetime of the provider.
  useEffect(() => {
    setUnauthorizedHandler(onUnauthorized)
    return () => setUnauthorizedHandler(null)
  }, [onUnauthorized])

  const value = useMemo(
    () => ({ user, loading, login, logout, onUnauthorized }),
    [user, loading, login, logout, onUnauthorized],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside an AuthProvider')
  return context
}
